import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiResponse } from "_/utils";
import { Machine } from "_/data/machines";
import { Uuid } from "_/types";
import {
  Buildset,
  KEYS as BUILDSET_KEYS,
  useBuildsets,
  buildBuildsetIdMap,
} from "_/data/buildsets";

const SCOPE = "channels";
export const KEYS = {
  all: () => [SCOPE],
  machines: (channelName: string) => [SCOPE, channelName, "machines"],
  buildsets: (channelName: string) => [SCOPE, channelName, "buildsets"],
};

export type Channel = {
  /// Unique channel name identifier.
  name: string;

  /// Git ref glob pattern used for the channel.
  refPattern: string;

  /// Brief human-readable description of the channel.
  description: string;

  /// Whether this channel is automatically tracked for new builds.
  autoTrack: boolean;

  /// Current target build for the channel.
  targetBuildset?: Uuid;

  updatedAt?: string;

  /// Total count of machines currently using this channel.
  totalMachines: number;

  /// Current count of machines that are up-to-date with the target build for the channel.
  currentMachines: number;
};

export type ChannelNameToBuildset = Record<string, Buildset | null>;

export async function getChannels(): Promise<Channel[]> {
  const response = await api.get<ApiResponse<Channel[]>>("/channels");
  return response.data.data;
}

export function useChannels() {
  return useQuery({
    queryKey: KEYS.all(),
    queryFn: getChannels,
  });
}

export async function getChannelMachines(
  channelName: string
): Promise<Machine[]> {
  const response = await api.get<ApiResponse<Machine[]>>(
    `/channels/${channelName}/machines`
  );
  return response.data.data;
}

export function useChannelMachines(name: string) {
  return useQuery({
    queryKey: KEYS.machines(name),
    queryFn: () => getChannelMachines(name),
  });
}

export async function setBuildsetOfChannel(
  channelName: string,
  buildsetId: Uuid
): Promise<Buildset> {
  const response = await api.post<ApiResponse<Buildset>>(
    `/channels/${channelName}/buildsets`,
    { buildsetId }
  );
  return response.data.data;
}

export function useSetBuildsetOfChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelName,
      buildsetId,
    }: {
      channelName: string;
      buildsetId: Uuid;
    }) => setBuildsetOfChannel(channelName, buildsetId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEYS.all() });
      void queryClient.invalidateQueries({ queryKey: BUILDSET_KEYS.all() });
    },
  });
}

// Maps channel names to their target buildsets (if any).
export function useChannelNameToBuildset() {
  const { data: channels = [] } = useChannels();
  const { data: buildsets = [] } = useBuildsets();

  const buildsetIdToBuildset = buildBuildsetIdMap(buildsets);

  const mapping: Record<string, Buildset | null> = {};
  channels.forEach((channel) => {
    mapping[channel.name] = channel.targetBuildset
      ? (buildsetIdToBuildset[channel.targetBuildset] ?? null)
      : null;
  });

  return mapping;
}
